import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Masked OTP Config Response Schema
 * Returns OTP configuration with secret masked for security
 */
export const MaskedOTPConfigSchema = z.object({
  targetId: z.string(),
  name: z.string(),
  algorithm: z.enum(["SHA1", "SHA256", "SHA512"]),
  digits: z.number().int(),
  period: z.number().int(),
  secretMasked: z.string().describe("Masked secret (e.g., '****...****')"),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Store Secret Input Schema
 * Input for storing a new OTP secret
 */
export const StoreSecretInputSchema = z.object({
  organizationId: z.string().uuid(),
  targetId: z
    .string()
    .regex(
      /^[a-zA-Z0-9-]+$/,
      "targetId must contain only alphanumeric characters and hyphens",
    )
    .max(100, "targetId must not exceed 100 characters"),
  name: z
    .string()
    .min(1, "name is required")
    .max(255, "name must not exceed 255 characters"),
  secret: z
    .string()
    .min(1, "secret is required")
    .describe("Base32-encoded OTP secret"),
  algorithm: z.enum(["SHA1", "SHA256", "SHA512"]).default("SHA1"),
  digits: z.number().int().min(4).max(10).default(6),
  period: z.number().int().positive().default(30),
});

/**
 * Get Config Input Schema
 */
export const GetConfigInputSchema = z.object({
  organizationId: z.string().uuid(),
  targetId: z.string(),
});

/**
 * Delete Secret Input Schema
 */
export const DeleteSecretInputSchema = z.object({
  organizationId: z.string().uuid(),
  targetId: z.string(),
});

/**
 * List Targets Input Schema
 */
export const ListTargetsInputSchema = z.object({
  organizationId: z.string().uuid(),
  limit: z.number().int().min(1).max(100).default(20),
  offset: z.number().int().min(0).default(0),
});

/**
 * List Targets Response Schema
 */
export const ListTargetsResponseSchema = z.object({
  configs: z.array(MaskedOTPConfigSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

/**
 * Delete Response Schema
 */
export const DeleteResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

/**
 * Test OTP Generation Input Schema (Admin-only)
 */
export const TestOTPInputSchema = z.object({
  organizationId: z.string().uuid(),
  targetId: z.string(),
});

/**
 * Test OTP Generation Response Schema
 */
export const TestOTPResponseSchema = z.object({
  success: z.boolean(),
  code: z.string().optional(),
  remainingSeconds: z.number().optional(),
  error: z.string().optional(),
});

/**
 * Agent SMS Number Schema
 * The org's provisioned inbound number for SMS-delivered OTP codes
 */
export const AgentSmsNumberSchema = z.object({
  id: z.string().uuid(),
  phoneNumber: z.string(),
  otpTargetId: z.string(),
  provider: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Get SMS Number Input Schema
 */
export const GetSmsNumberInputSchema = z.object({
  organizationId: z.string().uuid(),
});

/**
 * Register SMS Number Input Schema
 * Registers (or replaces) the single SMS OTP number for an organization
 */
export const RegisterSmsNumberInputSchema = z.object({
  organizationId: z.string().uuid(),
  phoneNumber: z
    .string()
    .regex(
      /^\+[1-9]\d{6,14}$/,
      "phoneNumber must be E.164 (e.g. +15551234567)",
    ),
  otpTargetId: z
    .string()
    .regex(
      /^[a-zA-Z0-9-]+$/,
      "otpTargetId must contain only alphanumeric characters and hyphens",
    )
    .max(100, "otpTargetId must not exceed 100 characters"),
});

/**
 * OTP Management Contracts
 */
export const otpContracts = {
  // Store a new OTP secret
  storeSecret: oc
    .input(StoreSecretInputSchema)
    .output(MaskedOTPConfigSchema)
    .route({
      method: "POST",
    }),

  // Get OTP configuration for a target
  getConfig: oc
    .input(GetConfigInputSchema)
    .output(MaskedOTPConfigSchema.nullable())
    .route({
      method: "GET",
    }),

  // Delete OTP secret for a target
  deleteSecret: oc
    .input(DeleteSecretInputSchema)
    .output(DeleteResponseSchema)
    .route({
      method: "DELETE",
    }),

  // List all OTP targets for an organization
  listTargets: oc
    .input(ListTargetsInputSchema)
    .output(ListTargetsResponseSchema)
    .route({
      method: "GET",
    }),

  // Admin-only: Test OTP code generation for a target
  testGenerate: oc
    .input(TestOTPInputSchema)
    .output(TestOTPResponseSchema)
    .route({
      method: "POST",
    }),

  // Get the org's SMS OTP number (null when none registered)
  getSmsNumber: oc
    .input(GetSmsNumberInputSchema)
    .output(AgentSmsNumberSchema.nullable())
    .route({
      method: "GET",
    }),

  // Register or replace the org's SMS OTP number
  registerSmsNumber: oc
    .input(RegisterSmsNumberInputSchema)
    .output(AgentSmsNumberSchema)
    .route({
      method: "POST",
    }),

  // Remove the org's SMS OTP number
  deleteSmsNumber: oc
    .input(GetSmsNumberInputSchema)
    .output(DeleteResponseSchema)
    .route({
      method: "DELETE",
    }),
} as const;

export type MaskedOTPConfig = z.infer<typeof MaskedOTPConfigSchema>;
export type StoreSecretInput = z.infer<typeof StoreSecretInputSchema>;
export type GetConfigInput = z.infer<typeof GetConfigInputSchema>;
export type DeleteSecretInput = z.infer<typeof DeleteSecretInputSchema>;
export type ListTargetsInput = z.infer<typeof ListTargetsInputSchema>;
export type ListTargetsResponse = z.infer<typeof ListTargetsResponseSchema>;
export type DeleteResponse = z.infer<typeof DeleteResponseSchema>;
export type TestOTPInput = z.infer<typeof TestOTPInputSchema>;
export type TestOTPResponse = z.infer<typeof TestOTPResponseSchema>;
export type AgentSmsNumber = z.infer<typeof AgentSmsNumberSchema>;
export type GetSmsNumberInput = z.infer<typeof GetSmsNumberInputSchema>;
export type RegisterSmsNumberInput = z.infer<
  typeof RegisterSmsNumberInputSchema
>;
